import type React from "react";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import LoadingSpinner from "../components/LoadingSpinner";
import { DateRangePicker } from "../components/ui/date-range-picker";
import useConfirmDialog from "../hooks/useConfirmDialog";

interface TransactionRow {
	transactionDate: string;
	status: string;
	partnerName: string;
	settlementAmount: number;
}

interface PartnerSummary {
	partnerName: string;
	completeAmount: number;
	readyAmount: number;
	totalAmount: number;
	transactionCount: number;
}

interface MonthlyData {
	month: string;
	amount: number;
	count: number;
}

const STORAGE_KEY = "settlement-analysis-data";

interface StoredData {
	fileName: string;
	transactions: TransactionRow[];
	uploadDate: string;
}

const SettlementAnalysis: React.FC = () => {
	const { confirm } = useConfirmDialog();
	const [fileName, setFileName] = useState<string>("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [transactions, setTransactions] = useState<TransactionRow[]>([]);
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");
	const [partnerFilter, setPartnerFilter] = useState<string>("all");
	const [quickFilter, setQuickFilter] = useState<string>("all");

	// Load saved data on component mount
	useEffect(() => {
		try {
			const savedData = localStorage.getItem(STORAGE_KEY);

			if (savedData) {
				const parsed = JSON.parse(savedData) as unknown;
				// Validate shape before using
				if (
					parsed &&
					typeof parsed === "object" &&
					"fileName" in parsed &&
					"transactions" in parsed &&
					Array.isArray((parsed as StoredData).transactions)
				) {
					const data = parsed as StoredData;
					setFileName(data.fileName);
					setTransactions(data.transactions);

					// Set initial date range
					if (data.transactions.length > 0) {
						const dates = data.transactions
							.map((t) => new Date(t.transactionDate))
							.filter((d) => !Number.isNaN(d.getTime()));
						if (dates.length > 0) {
							const minDate = new Date(
								Math.min(...dates.map((d) => d.getTime())),
							);
							const maxDate = new Date(
								Math.max(...dates.map((d) => d.getTime())),
							);
							setStartDate(minDate.toISOString().split("T")[0]);
							setEndDate(maxDate.toISOString().split("T")[0]);
						}
					}
				} else {
					// Invalid shape — clear corrupted data
					localStorage.removeItem(STORAGE_KEY);
				}
			}
		} catch {
			// Corrupted data — clear and ignore
			localStorage.removeItem(STORAGE_KEY);
		}
	}, []);

	// Save data whenever transactions change
	const saveData = (name: string, txns: TransactionRow[]) => {
		try {
			const dataToSave: StoredData = {
				fileName: name,
				transactions: txns,
				uploadDate: new Date().toISOString(),
			};
			localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
		} catch {
			// QuotaExceededError or serialization failure — silently skip persistence
		}
	};

	const clearSavedData = () => {
		localStorage.removeItem(STORAGE_KEY);
		setFileName("");
		setTransactions([]);
		setStartDate("");
		setEndDate("");
		setPartnerFilter("all");
		setQuickFilter("all");
		setError("");
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = e.target.files?.[0];
		if (selectedFile) {
			setError("");
			// Process file immediately like the HTML version
			processFile(selectedFile);
		}
		// Reset input so same file can be selected again
		e.target.value = "";
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		const droppedFile = e.dataTransfer.files?.[0];
		if (droppedFile) {
			setError("");
			processFile(droppedFile);
		}
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const parseDate = (value: unknown): Date | null => {
		if (!value) return null;
		if (typeof value === "number") {
			// Excel date serial number
			return new Date((value - 25569) * 86400 * 1000);
		}
		const str = String(value);
		const date = new Date(str);
		if (!Number.isNaN(date.getTime())) return date;

		// Try parsing MM/DD/YYYY format
		const parts = str.split("/");
		if (parts.length === 3) {
			const year = parseInt(parts[2], 10);
			const month = parseInt(parts[0], 10) - 1;
			const day = parseInt(parts[1], 10);
			if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
				return new Date(year, month, day);
			}
		}
		return null;
	};

	const parseAmount = (value: unknown): number => {
		if (!value) return 0;
		// Handle 'null' string explicitly
		if (value === "null" || value === "NULL" || value === "Null") return 0;
		if (typeof value === "number") return value;
		const cleaned = value.toString().replace(/[$,\s]/g, "");
		const amount = Number.parseFloat(cleaned);
		return Number.isNaN(amount) ? 0 : amount;
	};

	const processFile = async (fileToProcess: File) => {
		const validExtensions = [".csv", ".xlsx", ".xls"];
		const fileExt = fileToProcess.name
			.substring(fileToProcess.name.lastIndexOf("."))
			.toLowerCase();

		if (!validExtensions.includes(fileExt)) {
			setError("Please upload a valid CSV or Excel file.");
			return;
		}

		setLoading(true);
		setError("");

		try {
			const data = await fileToProcess.arrayBuffer();
			const workbook = XLSX.read(data, { type: "array", raw: false });
			const sheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[sheetName];
			const jsonData: unknown[][] = XLSX.utils.sheet_to_json(worksheet, {
				header: 1,
				raw: false,
			});

			// Column indices (0-based)
			// 0 = tran_evnt_date (Transaction Date)
			// 5 = Overall_Status (Status)
			// 15 = Wallet Name (Partner/Wallet Name)
			// 36 = Wallet Settlement Amt (Settlement Amount)
			const COL_DATE = 0;
			const COL_STATUS = 5;
			const COL_PARTNER = 15; // Changed from 11 (send_partner_name) to 15 (Wallet Name)
			const COL_AMOUNT = 36;

			// Parse the data - skip header row
			const parsedTransactions: TransactionRow[] = [];

			for (let i = 1; i < jsonData.length; i++) {
				const row = jsonData[i];
				if (!row || row.length === 0) continue;

				const date = parseDate(row[COL_DATE]);
				const status = (row[COL_STATUS] || "").toString().trim();
				const partnerName = (row[COL_PARTNER] || "").toString().trim();
				const settlementAmount = parseAmount(row[COL_AMOUNT]);

				// Filter by status
				const statusLower = status.toLowerCase();
				if (
					statusLower === "complete" ||
					statusLower === "ready for pickup" ||
					statusLower.includes("complete") ||
					statusLower.includes("ready")
				) {
					parsedTransactions.push({
						transactionDate: date ? date.toISOString().split("T")[0] : "",
						status,
						partnerName,
						settlementAmount,
					});
				}
			}

			if (parsedTransactions.length === 0) {
				setError(
					"No transactions found with status 'Complete' or 'Ready for Pickup'",
				);
				setLoading(false);
				return;
			}

			// Save file name and transactions
			setFileName(fileToProcess.name);
			setTransactions(parsedTransactions);
			saveData(fileToProcess.name, parsedTransactions);

			// Set initial date range
			const dates = parsedTransactions
				.map((t) => new Date(t.transactionDate))
				.filter((d) => !Number.isNaN(d.getTime()));
			if (dates.length > 0) {
				const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
				const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));
				setStartDate(minDate.toISOString().split("T")[0]);
				setEndDate(maxDate.toISOString().split("T")[0]);
			}

			setLoading(false);
		} catch (err: unknown) {
			const message = err instanceof Error ? err.message : String(err);
			setError(`Failed to parse file: ${message}`);
			setLoading(false);
		}
	};

	const handleQuickFilter = (period: string) => {
		setQuickFilter(period);

		if (transactions.length === 0) {
			return;
		}

		// Get the actual date range from the data
		const dates = transactions
			.map((t) => new Date(t.transactionDate))
			.filter((d) => !Number.isNaN(d.getTime()));

		if (dates.length === 0) {
			return;
		}

		const minDataDate = new Date(Math.min(...dates.map((d) => d.getTime())));
		const maxDataDate = new Date(Math.max(...dates.map((d) => d.getTime())));

		let start: Date, end: Date;

		switch (period) {
			case "thisMonth":
				// Last full month in the data
				end = new Date(
					maxDataDate.getFullYear(),
					maxDataDate.getMonth() + 1,
					0,
				);
				start = new Date(maxDataDate.getFullYear(), maxDataDate.getMonth(), 1);
				break;
			case "lastMonth":
				// Second to last month in the data
				end = new Date(maxDataDate.getFullYear(), maxDataDate.getMonth(), 0);
				start = new Date(
					maxDataDate.getFullYear(),
					maxDataDate.getMonth() - 1,
					1,
				);
				break;
			case "last3Months":
				// Last 3 months from the latest data date
				end = maxDataDate;
				start = new Date(
					maxDataDate.getFullYear(),
					maxDataDate.getMonth() - 2,
					1,
				);
				break;
			case "thisYear":
				// The year of the latest data
				start = new Date(maxDataDate.getFullYear(), 0, 1);
				end = new Date(maxDataDate.getFullYear(), 11, 31);
				break;
			default:
				// "all" - full data range
				start = minDataDate;
				end = maxDataDate;
		}

		const startDateStr = start.toISOString().split("T")[0];
		const endDateStr = end.toISOString().split("T")[0];

		setStartDate(startDateStr);
		setEndDate(endDateStr);
	};

	const getFilteredTransactions = (): TransactionRow[] => {
		if (transactions.length === 0) return [];

		let filtered = transactions;

		// Date filter
		if (startDate && endDate) {
			const startDateTime = new Date(startDate);
			const endDateTime = new Date(`${endDate}T23:59:59`);

			filtered = filtered.filter((t) => {
				const date = new Date(t.transactionDate);
				const isInRange = date >= startDateTime && date <= endDateTime;
				return isInRange;
			});
		}

		// Partner filter
		if (partnerFilter !== "all") {
			filtered = filtered.filter((t) => t.partnerName === partnerFilter);
		}

		return filtered;
	};

	const getPartnerSummaries = (): PartnerSummary[] => {
		const filtered = getFilteredTransactions();
		const summaryMap = new Map<string, PartnerSummary>();

		for (const transaction of filtered) {
			if (!transaction.partnerName) continue;

			const existing = summaryMap.get(transaction.partnerName);
			const statusLower = transaction.status.toLowerCase();
			const isComplete =
				statusLower === "complete" || statusLower.includes("complete");
			const isReady =
				statusLower === "ready for pickup" || statusLower.includes("ready");

			if (existing) {
				if (isComplete) existing.completeAmount += transaction.settlementAmount;
				if (isReady) existing.readyAmount += transaction.settlementAmount;
				existing.totalAmount += transaction.settlementAmount;
				existing.transactionCount += 1;
			} else {
				summaryMap.set(transaction.partnerName, {
					partnerName: transaction.partnerName,
					completeAmount: isComplete ? transaction.settlementAmount : 0,
					readyAmount: isReady ? transaction.settlementAmount : 0,
					totalAmount: transaction.settlementAmount,
					transactionCount: 1,
				});
			}
		}

		return Array.from(summaryMap.values()).sort(
			(a, b) => b.totalAmount - a.totalAmount,
		);
	};

	const getMonthlyBreakdown = (): MonthlyData[] => {
		const filtered = getFilteredTransactions();
		const monthlyMap = new Map<string, MonthlyData>();

		for (const transaction of filtered) {
			if (!transaction.transactionDate) continue;

			const date = new Date(transaction.transactionDate);
			if (Number.isNaN(date.getTime())) continue;

			const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

			const existing = monthlyMap.get(monthKey);
			if (existing) {
				existing.amount += transaction.settlementAmount;
				existing.count += 1;
			} else {
				monthlyMap.set(monthKey, {
					month: monthKey,
					amount: transaction.settlementAmount,
					count: 1,
				});
			}
		}

		return Array.from(monthlyMap.values()).sort((a, b) =>
			b.month.localeCompare(a.month),
		);
	};

	const formatCurrency = (amount: number): string => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
		}).format(amount);
	};

	const filtered = getFilteredTransactions();
	const completeData = filtered.filter(
		(t) =>
			t.status.toLowerCase() === "complete" ||
			t.status.toLowerCase().includes("complete"),
	);
	const readyData = filtered.filter(
		(t) =>
			t.status.toLowerCase() === "ready for pickup" ||
			t.status.toLowerCase().includes("ready"),
	);

	const grandTotal = filtered.reduce((sum, t) => sum + t.settlementAmount, 0);
	const completeTotal = completeData.reduce(
		(sum, t) => sum + t.settlementAmount,
		0,
	);
	const readyTotal = readyData.reduce((sum, t) => sum + t.settlementAmount, 0);
	const totalTransactions = filtered.length;
	const uniquePartners = new Set(
		filtered.map((t) => t.partnerName).filter((p) => p),
	).size;

	const partnerSummaries = getPartnerSummaries();
	const monthlyBreakdown = getMonthlyBreakdown();

	return (
		<div className="min-h-[calc(100vh-56px)] bg-(--color-background) px-6 pt-20 pb-8">
			<div className="mx-auto max-w-[1400px]">
				<h1 className="mb-8 font-bold text-(--color-foreground) text-3xl">
					Settlement Analysis
				</h1>

				{/* Upload Section */}
				{transactions.length === 0 && !loading && (
					// biome-ignore lint/a11y/useSemanticElements: Drop zone needs to be a div for drag-and-drop
					<div
						role="button"
						tabIndex={0}
						onClick={() => document.getElementById("fileInput")?.click()}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								document.getElementById("fileInput")?.click();
							}
						}}
						onDrop={handleDrop}
						onDragOver={handleDragOver}
						className="mb-8 cursor-pointer rounded-2xl border-(--color-border) border-2 border-dashed bg-(--color-card) p-12 text-center transition-all duration-300 hover:border-success-500 hover:bg-success-50"
					>
						<svg
							className="mx-auto mb-4 size-16 stroke-success-500"
							aria-hidden="true"
							fill="none"
							strokeWidth={2}
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"
							/>
						</svg>
						<p className="mb-2 font-medium text-(--color-foreground) text-lg">
							Drop your settlement file here or click to browse
						</p>
						<p className="text-(--color-muted-foreground) text-sm">
							Supports CSV and Excel files (.csv, .xlsx, .xls)
						</p>
						<p className="mt-4 text-gray-400 text-xs">
							Required columns: A (Transaction Date), F (Status), L (Partner
							Name), AK (Settlement Amount)
						</p>
						<input
							id="fileInput"
							type="file"
							accept=".xlsx,.xls,.csv"
							onChange={handleFileChange}
							className="hidden"
						/>
					</div>
				)}

				{/* File Info Banner */}
				{fileName && transactions.length > 0 && (
					<div className="mb-6 flex items-center gap-4 rounded-lg border border-success-300 bg-success-50 px-6 py-4">
						<svg
							className="size-10 shrink-0 fill-success-500"
							viewBox="0 0 24 24"
							aria-hidden="true"
						>
							<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
							<polyline points="14 2 14 8 20 8" />
							<line x1="16" y1="13" x2="8" y2="13" />
							<line x1="16" y1="17" x2="8" y2="17" />
							<polyline points="10 9 9 9 8 9" />
						</svg>
						<div className="flex-1">
							<div className="mb-1 font-semibold text-(--color-foreground)">
								{fileName}
							</div>
							<div className="text-sm text-success-600">
								✓ {transactions.length} transactions loaded • Data saved
							</div>
						</div>
						<div className="flex gap-2">
							<button
								type="button"
								onClick={() => document.getElementById("fileInput")?.click()}
								className="flex items-center gap-2 rounded-lg bg-success-500 px-4 py-2 font-medium text-sm text-white transition-colors hover:bg-success-600"
								title="Replace with a new file"
							>
								<svg
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={2}
									aria-hidden="true"
								>
									<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
									<polyline points="17 8 12 3 7 8" />
									<line x1="12" y1="3" x2="12" y2="15" />
								</svg>
								Replace File
							</button>
							<button
								type="button"
								onClick={async () => {
									const ok = await confirm({
										title: "Remove File",
										message:
											"Are you sure you want to remove this file? This will clear all data.",
										confirmLabel: "Remove",
										variant: "danger",
									});
									if (ok) clearSavedData();
								}}
								className="flex items-center gap-2 rounded-lg border border-(--color-border) bg-(--color-card) px-4 py-2 font-medium text-(--color-muted-foreground) text-sm transition-all hover:border-destructive hover:bg-destructive/5 hover:text-destructive"
								title="Remove file and clear data"
							>
								<svg
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									aria-hidden="true"
									strokeWidth={2}
								>
									<polyline points="3 6 5 6 21 6" />
									<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
								</svg>
								Remove File
							</button>
						</div>
					</div>
				)}

				{/* Loading State */}
				{loading && (
					<LoadingSpinner size="md" message="Processing your file..." />
				)}

				{/* Error Message */}
				{error && (
					<div className="mb-8 rounded-lg bg-destructive/10 p-4 text-destructive text-sm">
						{error}
					</div>
				)}

				{/* Results Section */}
				{transactions.length > 0 && (
					<>
						{/* Filter Section */}
						<div className="mb-8 rounded-lg bg-(--color-card) p-6 shadow-sm">
							<div className="mb-6 flex flex-wrap items-center justify-between gap-4">
								<h2 className="font-semibold text-xl">Filter Data</h2>
								<div className="flex flex-wrap gap-2">
									{[
										{ label: "All Time", value: "all" },
										{ label: "This Month", value: "thisMonth" },
										{ label: "Last Month", value: "lastMonth" },
										{ label: "Last 3 Months", value: "last3Months" },
										{ label: "This Year", value: "thisYear" },
									].map((filter) => (
										<button
											type="button"
											key={filter.value}
											onClick={() => handleQuickFilter(filter.value)}
											className={`rounded-full border px-4 py-2 font-medium text-sm transition-colors ${
												quickFilter === filter.value
													? "border-success-500 bg-success-500 text-white"
													: "border-(--color-border) bg-(--color-muted) text-(--color-muted-foreground) hover:bg-(--color-border)"
											}`}
										>
											{filter.label}
										</button>
									))}
								</div>
							</div>

							<div className="flex flex-wrap items-end gap-4">
								<div className="min-w-[300px] flex-1">
									<label
										htmlFor="date-range"
										className="mb-2 block font-medium text-(--color-muted-foreground) text-sm"
									>
										Date Range
									</label>
									<DateRangePicker
										id="date-range"
										startDate={startDate}
										endDate={endDate}
										onStartChange={(val) => {
											setStartDate(val);
											setQuickFilter("");
										}}
										onEndChange={(val) => {
											setEndDate(val);
											setQuickFilter("");
										}}
									/>
								</div>
								<div className="min-w-[200px] flex-1">
									<label
										htmlFor="partner-filter"
										className="mb-2 block font-medium text-(--color-muted-foreground) text-sm"
									>
										Partner
									</label>
									<select
										id="partner-filter"
										value={partnerFilter}
										onChange={(e) => setPartnerFilter(e.target.value)}
										className="w-full rounded-lg border border-(--color-border) bg-(--color-muted) px-4 py-3 text-sm"
									>
										<option value="all">All Partners</option>
										{[
											...new Set(
												transactions.map((t) => t.partnerName).filter((p) => p),
											),
										]
											.sort()
											.map((partner) => (
												<option key={partner} value={partner}>
													{partner}
												</option>
											))}
									</select>
								</div>
								<button
									type="button"
									onClick={() => {
										setPartnerFilter("all");
										setQuickFilter("all");
										handleQuickFilter("all");
									}}
									className="rounded-lg border border-(--color-border) bg-(--color-muted) px-6 py-3 font-medium text-(--color-foreground) text-sm hover:bg-(--color-border)"
								>
									Reset
								</button>
							</div>
						</div>

						{/* Summary Cards */}
						<div className="mb-8 grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4">
							<div className="rounded-lg border-2 border-success-600 bg-(--color-card) p-6 shadow-sm">
								<div className="mb-2 font-medium text-(--color-muted-foreground) text-sm uppercase tracking-wide">
									Total Settlement Amount
								</div>
								<div className="font-bold text-3xl text-success-600">
									{formatCurrency(grandTotal)}
								</div>
								<div className="mt-2 text-(--color-muted-foreground) text-sm">
									{totalTransactions} transactions
								</div>
							</div>
							<div className="rounded-lg bg-(--color-card) p-6 shadow-sm">
								<div className="mb-2 font-medium text-(--color-muted-foreground) text-sm uppercase tracking-wide">
									Complete Status
								</div>
								<div className="font-bold text-(--color-foreground) text-3xl">
									{formatCurrency(completeTotal)}
								</div>
								<div className="mt-2 text-(--color-muted-foreground) text-sm">
									{completeData.length} transactions
								</div>
							</div>
							<div className="rounded-lg bg-(--color-card) p-6 shadow-sm">
								<div className="mb-2 font-medium text-(--color-muted-foreground) text-sm uppercase tracking-wide">
									Ready for Pickup
								</div>
								<div className="font-bold text-(--color-foreground) text-3xl">
									{formatCurrency(readyTotal)}
								</div>
								<div className="mt-2 text-(--color-muted-foreground) text-sm">
									{readyData.length} transactions
								</div>
							</div>
							<div className="rounded-lg bg-(--color-card) p-6 shadow-sm">
								<div className="mb-2 font-medium text-(--color-muted-foreground) text-sm uppercase tracking-wide">
									Unique Partners
								</div>
								<div className="font-bold text-(--color-foreground) text-3xl">
									{uniquePartners}
								</div>
								<div className="mt-2 text-(--color-muted-foreground) text-sm">
									Active partners
								</div>
							</div>
						</div>

						{/* Partner Summary Table */}
						<div className="overflow-hidden rounded-lg bg-(--color-card) shadow-sm">
							<div className="flex items-center justify-between border-(--color-border) border-b px-6 py-4">
								<h2 className="font-semibold text-xl">Settlement by Partner</h2>
								<button
									type="button"
									onClick={() => {
										const headers = [
											"Partner Name",
											"Complete Amount",
											"Ready for Pickup Amount",
											"Total Amount",
											"Transaction Count",
										];
										const rows = partnerSummaries.map((p) => [
											p.partnerName,
											p.completeAmount,
											p.readyAmount,
											p.totalAmount,
											p.transactionCount,
										]);
										const csvContent = [headers, ...rows]
											.map((row) => row.map((cell) => `"${cell}"`).join(","))
											.join("\n");
										const blob = new Blob([csvContent], { type: "text/csv" });
										const url = URL.createObjectURL(blob);
										const a = document.createElement("a");
										a.href = url;
										a.download = `settlement_by_partner_${new Date().toISOString().split("T")[0]}.csv`;
										a.click();
										URL.revokeObjectURL(url);
									}}
									className="flex items-center gap-2 rounded-md border border-(--color-border) bg-(--color-muted) px-4 py-2 font-medium text-sm hover:bg-(--color-border)"
								>
									<svg
										className="size-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										strokeWidth={2}
										aria-hidden="true"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
										/>
									</svg>
									Export CSV
								</button>
							</div>
							<div className="overflow-x-auto">
								<table className="w-full border-collapse">
									<thead className="bg-(--color-muted)">
										<tr>
											<th className="px-6 py-3 text-left font-semibold text-(--color-foreground) text-xs uppercase tracking-wide">
												Partner Name
											</th>
											<th className="px-6 py-3 text-right font-semibold text-(--color-foreground) text-xs uppercase tracking-wide">
												Complete Amount
											</th>
											<th className="px-6 py-3 text-right font-semibold text-(--color-foreground) text-xs uppercase tracking-wide">
												Ready for Pickup
											</th>
											<th className="px-6 py-3 text-right font-semibold text-(--color-foreground) text-xs uppercase tracking-wide">
												Total Amount
											</th>
											<th className="px-6 py-3 text-right font-semibold text-(--color-foreground) text-xs uppercase tracking-wide">
												Transaction Count
											</th>
										</tr>
									</thead>
									<tbody>
										{partnerSummaries.map((partner, index) => (
											<tr
												key={`partner-${partner.partnerName || ""}-${index}`}
												className="border-(--color-border) border-b"
											>
												<td className="px-6 py-4 font-medium text-sm">
													{partner.partnerName}
												</td>
												<td className="px-6 py-4 text-right text-sm text-success-600">
													{formatCurrency(partner.completeAmount)}
												</td>
												<td className="px-6 py-4 text-right text-sm text-success-600">
													{formatCurrency(partner.readyAmount)}
												</td>
												<td className="px-6 py-4 text-right font-semibold text-sm">
													{formatCurrency(partner.totalAmount)}
												</td>
												<td className="px-6 py-4 text-right text-(--color-muted-foreground) text-sm">
													{partner.transactionCount}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>

						{/* Monthly Breakdown */}
						{monthlyBreakdown.length > 0 && (
							<div className="mt-8">
								<h3 className="mb-4 font-semibold text-xl">
									Monthly Breakdown
								</h3>
								<div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
									{monthlyBreakdown.map((month, index) => {
										const [year, monthNum] = month.month.split("-");
										const monthName = new Date(
											Number.parseInt(year, 10),
											Number.parseInt(monthNum, 10) - 1,
										).toLocaleDateString("en-US", {
											month: "long",
											year: "numeric",
										});

										return (
											<div
												key={`summary-${month.month}-${index}`}
												className="rounded-xl border border-(--color-border) bg-(--color-card) p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-success-600"
											>
												<div className="mb-2 text-(--color-muted-foreground) text-sm">
													{monthName}
												</div>
												<div className="font-mono font-semibold text-success-600 text-xl">
													{formatCurrency(month.amount)}
												</div>
												<div className="mt-1 text-gray-400 text-xs">
													{month.count} transactions
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default SettlementAnalysis;
